import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, ClickAwayListener, Stack, Typography, useTheme } from '@mui/material'
import { components } from 'react-select'
import StateIcon from 'components/global/StateIcon'
import { OptionMultiCount } from 'components/global/CUK/AsyncSelect/components/Option'
import { Select } from 'components/global/CUK/AsyncSelect'

const ClearIndicator = ({ ...props }) => {
  const theme = useTheme()

  const handleMouseDown = event => {
    event.preventDefault()
    event.stopPropagation()
    props.clearValue()
  }

  const handleClick = event => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleKeyDown = event => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    props.clearValue()
  }

  return (
    <Box
      component="button"
      type="button"
      aria-label="Effacer les modèles sélectionnés"
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        alignItems: 'center',
        background: 'transparent',
        border: 0,
        color: theme.palette.grey[500],
        cursor: 'pointer',
        display: 'flex',
        padding: 0,
        '&:hover': {
          color: theme.palette.grey[800]
        }
      }}>
      <StateIcon iconlabel={'xmark'} iconweight={'regular'} iconcolor="currentColor" iconsize={12} />
    </Box>
  )
}

const DropdownIndicator = ({ ...props }) => {
  const theme = useTheme()
  return (
    <Box mr={1}>
      {props.selectProps.menuIsOpen ? (
        <StateIcon iconlabel={'angle-up'} iconweight={'regular'} iconcolor={theme.palette.primary.main} />
      ) : (
        <StateIcon iconweight={'regular'} iconlabel={'angle-down'} />
      )}
    </Box>
  )
}

const ValueContainer = ({ selectedMessageKey, ...props }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const selectedValuesCount = props.getValue().length
  const { menuIsOpen } = props.selectProps
  
  return (
    <components.ValueContainer {...props}>
      <Box>
        <Typography
          sx={{
            color: menuIsOpen ? '#3B82F6' : theme.palette.grey[500],
            fontSize: '12px',
            paddingLeft: theme.spacing(1.5),
            fontWeight: 500
          }}
        >
          {selectedValuesCount === 0
            ? props.selectProps.placeholder
            : t(selectedMessageKey ?? 'supervision.filters.selected_placeholder', { count: selectedValuesCount})}
        </Typography>
      </Box>
    </components.ValueContainer>
  )
}

const Option = props => {
  return <OptionMultiCount {...props} value={props.data} count={props.data.count} availability={props.data.value} />
}

const StatusDevicesSelect = ({ placeholder, noOptionsMessage, options, onChange, values = [], ...selectProps }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  return (
    <Select
      isMulti
      isClearable
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={options}
      onChange={values => onChange(values.map(({ value }) => value))}
      mainField="label"
      placeholder={placeholder}
      noOptionsMessage={noOptionsMessage}
      isSearchable={true}
      components={{
        Option,
        Control: ({ children, ...props }) => {
          const { menuIsOpen, onMenuClose, onMenuOpen } = props.selectProps
          return (
            <components.Control {...props}>
              <ClickAwayListener
                mouseEvent="onMouseDown"
                onClickAway={() => {
                  onMenuClose()
                }}
              >
                <Box
                  onClick={() => {
                    if (menuIsOpen) {
                      onMenuClose()
                    } else {
                      onMenuOpen()
                    }
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexGrow: 1,
                    gap: 7,
                    cursor: 'pointer'
                  }}
                >
                  {children}
                </Box>
              </ClickAwayListener>
            </components.Control>
          )
        },
        NoOptionsMessage: ({ children }) => {
          return (
            <Stack sx={{fontSize: 13, color: 'grey.400'}} ml={1}>{children}</Stack>
          )
        },
        MenuList: ({ children, ...props }) => {
          return (
            <components.MenuList {...props}>
              {children}
              {
                children.length > 0 && props.getValue().length > 0 &&  (
                  <Box
                    ml={1}
                    mt={1}
                    color={'grey.600'}
                    fontSize={12}
                    fontWeight={500}
                    onClick={() => props.setValue([])}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.grey[800]
                      }
                    }}
                  >
                    {t('actions.deselect_all')}
                  </Box>
                )
              }
            </components.MenuList>
          )
        },
        DropdownIndicator,
        ValueContainer: props => <ValueContainer selectedMessageKey={selectProps.selectedMessageKey} {...props} />,
        Placeholder: props => (
          <Box>
            <components.Placeholder {...props} />
          </Box>
        ),
        IndicatorSeparator: null,
        ClearIndicator
      }}
      value={options.filter(choice => values.findIndex(value => choice.value === value) > -1)}
      {...selectProps}
    />
  )
}

export default StatusDevicesSelect
